import { useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useExtendLicense } from "@/api/mutations/licenses";
import type { License } from "@/types";

// Verlaengern: nur in die Zukunft (nie verkuerzen — Server validiert final). Bei
// Trial-Lizenzen optional Trial→Kauf-Konversion (trial:false, nie zurueck).
export function ExtendLicenseDialog({
  open,
  onOpenChange,
  license,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  license: License;
}) {
  const [validUntil, setValidUntil] = useState("");
  const [convert, setConvert] = useState(false);
  const extend = useExtendLicense();

  const currentUntilDay = license.validUntil.slice(0, 10);
  // Client-Hinweis (Server ist Final-Validator): neues Datum muss nach dem aktuellen liegen.
  const tooEarly = validUntil !== "" && validUntil <= currentUntilDay;

  function submit(e: FormEvent) {
    e.preventDefault();
    extend.mutate(
      {
        id: license.id,
        validUntil: new Date(`${validUntil}T23:59:59Z`).toISOString(),
        ...(license.trial && convert ? { trial: false } : {}),
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="extend-license-dialog">
        <DialogHeader>
          <DialogTitle>Lizenz verlaengern</DialogTitle>
          <DialogDescription>
            Aktuell gueltig bis {currentUntilDay}. Das naechste Lease traegt das neue Datum automatisch —
            kein neuer Schluessel, kein Kunden-Zutun.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ext-until">Neues Gueltig-bis</Label>
            <Input id="ext-until" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} required data-testid="extend-until" />
            {tooEarly && (
              <div className="text-xs text-warn" data-testid="extend-warn">
                Muss nach dem aktuellen Datum ({currentUntilDay}) liegen — Verkuerzen ist nicht moeglich.
              </div>
            )}
          </div>
          {license.trial && (
            <label className="flex items-center gap-2 text-sm text-text2">
              <input type="checkbox" checked={convert} onChange={(e) => setConvert(e.target.checked)} data-testid="extend-convert" />
              Trial → Kauf umwandeln
            </label>
          )}
          {extend.isError && (
            <div className="text-sm text-danger" data-testid="extend-error">
              {extend.error.message}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={extend.isPending}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={extend.isPending || tooEarly || validUntil === ""} data-testid="extend-submit">
              {extend.isPending ? "…" : "Verlaengern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
